// apps/web/src/components/analisis/FiltrosPanel.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Calendar, BarChart3 } from 'lucide-react';
import { FiltrosDisponibles } from '../../services/analisisService';

// ===============================
// INTERFACES
// ===============================

export interface FiltrosFormData {
  fechaDesde: string;
  fechaHasta: string;
  maquinasIds: number[];
  operadoresIds: number[];
  faenasIds: number[];
  prediosIds: number[];
  rodalesIds: number[];
  incluirSuperposiciones: boolean;
}

interface FiltrosPanelProps {
  filtrosDisponibles: FiltrosDisponibles | null;
  filtros: FiltrosFormData;
  setFiltros: React.Dispatch<React.SetStateAction<FiltrosFormData>>;
  onEjecutarAnalisis: () => void;
  cargandoAnalisis: boolean;
  autoEjecutar: boolean; // 🆕 Nuevo prop
  onToggleAutoEjecutar: (enabled: boolean) => void; // 🆕 Nuevo prop
}

// ===============================
// COMPONENTE
// ===============================

export const FiltrosPanel: React.FC<FiltrosPanelProps> = (
  {
    filtrosDisponibles,
    filtros,
    setFiltros,
    onEjecutarAnalisis,
    cargandoAnalisis,
    autoEjecutar, // 🆕 Nuevo prop
    onToggleAutoEjecutar // 🆕 Nuevo prop
  }
) => {
  const { t } = useTranslation();
  const limpiarFiltros = () => {
    setFiltros({
      fechaDesde: filtrosDisponibles?.rango_fechas.fecha_min || '',
      fechaHasta: filtrosDisponibles?.rango_fechas.fecha_max || '',
      maquinasIds: [],
      operadoresIds: [],
      faenasIds: [],
      prediosIds: [],
      rodalesIds: [],
      incluirSuperposiciones: false
    });
  };

  const tienesFiltrosActivos = () => {
    return filtros.maquinasIds.length > 0 || 
           filtros.operadoresIds.length > 0 || 
           filtros.faenasIds.length > 0 || 
           filtros.prediosIds.length > 0 || 
           filtros.rodalesIds.length > 0;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center mb-4">
        <Filter className="w-5 h-5 text-gray-600 mr-2" />
        <h2 className="text-lg font-medium text-gray-900">{t('analysis.filters')}</h2>
      </div>

      <div className="space-y-4">
        {/* Filtros de Fecha */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            {t('analysis.date_from')} / {t('analysis.date_to')}
          </label>
          <div className="grid grid-cols-1 gap-2">
            <input
              type="date"
              value={filtros.fechaDesde}
              onChange={(e) => setFiltros(prev => ({ ...prev, fechaDesde: e.target.value }))}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
            <input
              type="date"
              value={filtros.fechaHasta}
              onChange={(e) => setFiltros(prev => ({ ...prev, fechaHasta: e.target.value }))}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Filtro de Máquinas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            🚜 {t('analysis.machines')} ({filtrosDisponibles?.maquinas.length || 0})
          </label>
          <select
            multiple
            value={filtros.maquinasIds.map(id => id.toString())}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => parseInt(option.value));
              setFiltros(prev => ({ ...prev, maquinasIds: values }));
            }}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            size={4}
          >
            {filtrosDisponibles?.maquinas.map(maquina => (
              <option key={maquina.id} value={maquina.id}>
                {maquina.codigo} - {maquina.descripcion}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Ctrl+Click. {filtros.maquinasIds.length} {t('common.select')}.
          </p>
        </div>

        {/* Filtro de Operadores */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            👨‍💼 {t('analysis.operators')} ({filtrosDisponibles?.operadores.length || 0})
          </label>
          <select
            multiple
            value={filtros.operadoresIds.map(id => id.toString())}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => parseInt(option.value));
              setFiltros(prev => ({ ...prev, operadoresIds: values }));
            }}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            size={4}
          >
            {filtrosDisponibles?.operadores.map(operador => (
              <option key={operador.id} value={operador.id}>
                {operador.codigo} - {operador.descripcion}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Ctrl+Click. {filtros.operadoresIds.length} {t('common.select')}.
          </p>
        </div>

        {/* Filtro de Faenas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            🏗️ {t('analysis.faenas')} ({filtrosDisponibles?.faenas.length || 0})
          </label>
          <select
            multiple
            value={filtros.faenasIds.map(id => id.toString())}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => parseInt(option.value));
              setFiltros(prev => ({ ...prev, faenasIds: values }));
            }}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            size={4}
          >
            {filtrosDisponibles?.faenas.map(faena => (
              <option key={faena.id} value={faena.id}>
                {faena.codigo} - {faena.descripcion}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Ctrl+Click. {filtros.faenasIds.length} {t('common.select')}.
          </p>
        </div>

        {/* Filtro de Predios */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            🏞️ {t('analysis.predios')} ({filtrosDisponibles?.predios.length || 0})
          </label>
          <select
            multiple
            value={filtros.prediosIds.map(id => id.toString())}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => parseInt(option.value));
              setFiltros(prev => ({ ...prev, prediosIds: values }));
            }}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            size={4}
          >
            {filtrosDisponibles?.predios.map(predio => (
              <option key={predio.id} value={predio.id}>
                {predio.codigo} - {predio.descripcion}
                {predio.superficie_hectareas && ` (${predio.superficie_hectareas.toFixed(2)} ha)`}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Ctrl+Click. {filtros.prediosIds.length} {t('common.select')}.
          </p>
        </div>

        {/* Filtro de Rodales */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            🌲 {t('analysis.rodales')} ({filtrosDisponibles?.rodales.length || 0})
          </label>
          <select
            multiple
            value={filtros.rodalesIds.map(id => id.toString())}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => parseInt(option.value));
              setFiltros(prev => ({ ...prev, rodalesIds: values }));
            }}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            size={4}
          >
            {filtrosDisponibles?.rodales.map(rodal => (
              <option key={rodal.id} value={rodal.id}>
                {rodal.codigo} - {rodal.descripcion}
                {rodal.superficie_hectareas && ` (${rodal.superficie_hectareas.toFixed(2)} ha)`}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Ctrl+Click. {filtros.rodalesIds.length} {t('common.select')}.
          </p>
        </div>

        {/* Opciones */}
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filtros.incluirSuperposiciones}
              onChange={(e) => setFiltros(prev => ({ ...prev, incluirSuperposiciones: e.target.checked }))}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              {t('analysis.multi_criteria', 'Analizar superposiciones')}
            </span>
          </label>
          
          {/* 🆕 Toggle para auto-ejecución */}
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={autoEjecutar}
              onChange={(e) => onToggleAutoEjecutar(e.target.checked)}
              className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-500 focus:ring-green-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              🔄 {t('analysis.results', 'Auto-actualizar resultados')}
            </span>
          </label>
          {autoEjecutar && (
            <p className="text-xs text-green-600 ml-6">
              {t('analysis.results', 'Los resultados se actualizarán automáticamente al cambiar filtros')}
            </p>
          )}
        </div>

        {/* Botón de Análisis */}
        <button
          onClick={onEjecutarAnalisis}
          disabled={cargandoAnalisis}
          className={`w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
            autoEjecutar 
              ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
              : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
          }`}
        >
          {cargandoAnalisis ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('analysis.running')}
            </>
          ) : autoEjecutar ? (
            <>
              <div className="animate-pulse w-4 h-4 bg-white rounded-full mr-2"></div>
              {t('analysis.results', 'Auto-actualización ON')}
            </>
          ) : (
            <>
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('analysis.run_analysis')}
            </>
          )}
        </button>

        {/* Botón de Limpiar Filtros */}
        <div className="pt-2 border-t border-gray-200">
          <button
            onClick={limpiarFiltros}
            className="w-full text-sm text-gray-600 hover:text-gray-800 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            🗺️ {t('common.clear_filters')}
          </button>
        </div>

        {/* Resumen de Filtros Activos */}
        {tienesFiltrosActivos() && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <h4 className="text-sm font-medium text-blue-800 mb-2">📊 {t('analysis.filters')}:</h4>
            <div className="space-y-1 text-xs text-blue-700">
              {filtros.maquinasIds.length > 0 && (
                <div>🚜 {filtros.maquinasIds.length} {t('analysis.machines')}</div>
              )}
              {filtros.operadoresIds.length > 0 && (
                <div>👨‍💼 {filtros.operadoresIds.length} {t('analysis.operators')}</div>
              )}
              {filtros.faenasIds.length > 0 && (
                <div>🏗️ {filtros.faenasIds.length} {t('analysis.faenas')}</div>
              )}
              {filtros.prediosIds.length > 0 && (
                <div>🏞️ {filtros.prediosIds.length} {t('analysis.predios')}</div>
              )}
              {filtros.rodalesIds.length > 0 && (
                <div>🌲 {filtros.rodalesIds.length} {t('analysis.rodales')}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FiltrosPanel;